import { useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";

import { getJeepRallyMediaUrl } from "../../../../api/jeep-rally/api";
import { useUploadGalleryImageMutation } from "../../../../api/jeep-rally/gallery/gallery.hooks";

const getUploadErrorMessage = (error) => {
  const data = error?.response?.data;
  return (
    data?.message ||
    (typeof data?.error === "string" ? data.error : "") ||
    error?.message ||
    "Failed to upload image."
  );
};

const MediaField = ({
  label,
  value,
  disabled = false,
  error = "",
  onChange,
  onUploadStart,
  onUploadEnd,
}) => {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState("");
  const {
    mutateAsync: uploadImage,
    isLoading,
    isPending,
  } = useUploadGalleryImageMutation();

  const uploading = isLoading || isPending;
  const previewUrl = getJeepRallyMediaUrl(value);
  const displayError = localError || error;

  const handlePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled) return;

    setLocalError("");
    onUploadStart?.();
    try {
      const result = await uploadImage({ image: file });
      const imageUrl =
        result?.image_url || result?.imageUrl || result?.url || "";
      if (!imageUrl) {
        setLocalError("Upload succeeded but no image URL was returned.");
        return;
      }
      onChange(imageUrl);
    } catch (uploadError) {
      setLocalError(getUploadErrorMessage(uploadError));
    } finally {
      onUploadEnd?.();
    }
  };

  return (
    <Form.Group className="mb-3">
      {label ? <Form.Label>{label}</Form.Label> : null}

      <div className="d-flex flex-wrap gap-3 align-items-start">
        <div
          className="d-flex align-items-center justify-content-center overflow-hidden"
          style={{
            width: 120,
            height: 80,
            borderRadius: 6,
            border: "1px solid #dee2e6",
            flexShrink: 0,
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={label || "Preview"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span className="text-muted small">No image</span>
          )}
        </div>

        <div className="flex-grow-1" style={{ minWidth: 200 }}>
          <div
            className="mb-2 px-2 py-2 border rounded small text-break"
            title={value || undefined}
          >
            {value ? (
              <code className="text-body">{value}</code>
            ) : (
              <span className="text-muted">No path yet — upload an image</span>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handlePick}
              disabled={disabled || uploading}
            />
            <Button
              type="button"
              size="sm"
              variant="outline-primary"
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Uploading...
                </>
              ) : (
                "Upload image"
              )}
            </Button>
          </div>
          {displayError ? (
            <div className="text-danger small mt-1">{displayError}</div>
          ) : (
            <div className="form-text">
              Uploads to gallery, then stores the returned path.
            </div>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

export default MediaField;
